/**
 * RhythmEngine.ts - 120 BPM generative rhythm system for Rhythm mode
 * 
 * Generates kick/snare patterns and manages timing:
 * - Kick (bombo): 4/4 (beats on 1, 2, 3, 4)
 * - Snare (caja): 2/4 (beats on 2 and 4)
 * - Occasional ghost/variation snares
 * - Player must tap to snare rhythm
 */

const BPM = 120;
const BEAT_INTERVAL_MS = (60 / BPM) * 1000; // 500ms per beat at 120 BPM

export type BeatType = 'kick' | 'snare' | 'ghost-snare';
export type ButtonColor = 'red' | 'green' | 'blue' | 'yellow';

export interface BeatEvent {
  type: BeatType;
  time: number; // Absolute time in ms from start
  beatNumber: number; // 1-4 within measure
  isTapTarget: boolean; // True if player should tap this
  // NO targetColor - player must remember from sequence
}

export interface TapFeedback {
  success: boolean;
  timing: 'perfect' | 'good' | 'late' | 'early' | 'miss';
  delta: number; // ms difference from expected
}

export class RhythmEngine {
  private startTime: number = 0;
  private isRunning: boolean = false;
  private events: BeatEvent[] = [];
  private currentEventIndex: number = 0;
  
  // Configuration
  private readonly PERFECT_WINDOW_MS = 75; // ±75ms
  private readonly GOOD_WINDOW_MS = 150; // ±150ms
  private readonly MAX_WINDOW_MS = 250; // ±250ms (miss beyond this)
  
  /**
   * Start the rhythm engine and generate initial pattern
   * @param sequenceLength Length of sequence (number of taps expected)
   */
  start(sequenceLength: number): void {
    this.startTime = Date.now();
    this.isRunning = true;
    this.currentEventIndex = 0;
    this.events = this.generatePattern(sequenceLength);
  }
  
  /**
   * Stop the rhythm engine
   */
  stop(): void {
    this.isRunning = false;
    this.events = [];
    this.currentEventIndex = 0;
  }
  
  /**
   * Generate rhythm pattern for specified sequence length
   * Simple pattern: one neutral timing mark per sequence item
   * Each mark happens every 2000ms (when snare plays)
   * NO color information - player must remember sequence
   */
  private generatePattern(sequenceLength: number): BeatEvent[] {
    const events: BeatEvent[] = [];
    const SNARE_INTERVAL_MS = 2000; // Every 4 beats at 120 BPM
    
    // Generate one tap target per sequence item
    for (let i = 0; i < sequenceLength; i++) {
      const tapTime = i * SNARE_INTERVAL_MS;
      
      events.push({
        type: 'snare',
        time: tapTime,
        beatNumber: (i % 4) + 1,
        isTapTarget: true,
        // NO targetColor - timeline shows neutral marks only
      });
    }
    
    return events.sort((a, b) => a.time - b.time);
  }
  
  /**
   * Get all events (for rendering timeline)
   */
  getEvents(): BeatEvent[] {
    return [...this.events];
  }
  
  /**
   * Get events that should be tapped (snares and ghost-snares)
   */
  getTapTargets(): BeatEvent[] {
    return this.events.filter(e => e.isTapTarget);
  }
  
  /**
   * Get current elapsed time since start
   */
  getCurrentTime(): number {
    if (!this.isRunning) return 0;
    return Date.now() - this.startTime;
  }
  
  /**
   * Get next expected tap event
   */
  getNextTapTarget(): BeatEvent | null {
    const tapTargets = this.getTapTargets();
    if (this.currentEventIndex >= tapTargets.length) return null;
    return tapTargets[this.currentEventIndex];
  }
  
  /**
   * Validate a tap and provide feedback
   */
  validateTap(tapTime: number = Date.now()): TapFeedback {
    const nextTarget = this.getNextTapTarget();
    
    if (!nextTarget) {
      return {
        success: false,
        timing: 'miss',
        delta: 0,
      };
    }
    
    const expectedTime = this.startTime + nextTarget.time;
    const delta = tapTime - expectedTime;
    const absDelta = Math.abs(delta);
    
    // Check timing windows
    if (absDelta <= this.PERFECT_WINDOW_MS) {
      this.currentEventIndex++;
      return {
        success: true,
        timing: 'perfect',
        delta,
      };
    } else if (absDelta <= this.GOOD_WINDOW_MS) {
      this.currentEventIndex++;
      return {
        success: true,
        timing: 'good',
        delta,
      };
    } else if (absDelta <= this.MAX_WINDOW_MS) {
      // Still counts as success but poor timing
      this.currentEventIndex++;
      const timing = delta < 0 ? 'early' : 'late';
      return {
        success: true,
        timing,
        delta,
      };
    }
    
    // Missed the window
    return {
      success: false,
      timing: 'miss',
      delta,
    };
  }
  
  /**
   * Check if all tap targets have been hit
   */
  isComplete(): boolean {
    return this.currentEventIndex >= this.getTapTargets().length;
  }
  
  /**
   * Get progress (0.0 to 1.0)
   */
  getProgress(): number {
    const tapTargets = this.getTapTargets();
    if (tapTargets.length === 0) return 0;
    return this.currentEventIndex / tapTargets.length;
  }
  
  /**
   * Reset to beginning
   */
  reset(): void {
    this.currentEventIndex = 0;
  }
  
  /**
   * Get BPM constant
   */
  getBPM(): number {
    return BPM;
  }
  
  /**
   * Get beat interval in ms
   */
  getBeatInterval(): number {
    return BEAT_INTERVAL_MS;
  }
}
